<template>
  <div v-click-outside="onClickOutside" class="timepicker-dropdown">
    <b-field :label="label">
      <b-input
        v-model="displayValue"
        class="tp-input-time"
        readonly
        icon="clock-outline"
        @focus="open = true"
        @click.native="open = true"
      />
    </b-field>
    <transition name="fade">
      <div v-if="open" class="picker-wrapper">
        <div class="time-picker">
          <div class="header">
            <div
              class="box"
              :class="{ active: mode === 'hour' }"
              @click="mode = 'hour'"
            >
              {{ displayHour }}
            </div>
            <span class="colon">:</span>
            <div
              class="box"
              :class="{ active: mode === 'minute' }"
              @click="
                mode = 'minute';
                automatic = false;
              "
            >
              {{ displayMinute }}
            </div>
            <div class="ampm">
              <button
                :class="{ active: period === 'AM' }"
                @click="setPeriod('AM')"
              >
                AM
              </button>
              <button
                :class="{ active: period === 'PM' }"
                @click="setPeriod('PM')"
              >
                PM
              </button>
            </div>
          </div>
          <div class="clock" @click="onClockClick">
            <div
              v-for="n in clockValues"
              :key="n"
              class="number"
              :style="position(n)"
              @click="select(n)"
            >
              {{ n }}
            </div>
            <div class="hand" :style="handStyle"></div>
            <div class="center"></div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: "TimePicker",
  directives: {
    clickOutside: {
      bind(el, binding) {
        el.__handler__ = (e) => {
          if (!el.contains(e.target) && typeof binding.value === "function") {
            binding.value(e);
          }
        };
        document.addEventListener("mousedown", el.__handler__);
        document.addEventListener("touchstart", el.__handler__);
      },
      unbind(el) {
        document.removeEventListener("mousedown", el.__handler__);
        document.removeEventListener("touchstart", el.__handler__);
      },
    },
  },
  props: {
    value: {
      type: Date,
      default: () => new Date(),
    },
    label: {
      type: String,
      default: "Label",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const hour = this.value.getHours();
    const minute = this.value.getMinutes();
    const period = hour >= 12 ? "PM" : "AM";

    return {
      open: false,
      hour: hour % 12 || 12,
      minute: minute,
      period: period,
      mode: "hour",
      automatic: false,
    };
  },
  computed: {
    displayValue() {
      const hour = this.displayHour;
      const minute = this.displayMinute;

      return `${hour}:${minute} ${this.period}`;
    },
    clockValues() {
      return this.mode === "hour"
        ? [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11]
        : [
            "00",
            "05",
            "10",
            "15",
            "20",
            "25",
            "30",
            "35",
            "40",
            "45",
            "50",
            "55",
          ];
    },
    displayHour() {
      return String(this.hour).padStart(2, "0");
    },
    displayMinute() {
      return String(this.minute).padStart(2, "0");
    },
    handStyle() {
      let angle;

      if (this.mode === "hour") {
        angle = (this.hour % 12) * 30;
      } else {
        angle = (Number(this.minute) / 60) * 360;
      }

      return {
        transform: `translate(-50%, -100%) rotate(${angle}deg)`,
      };
    },
  },
  methods: {
    position(value) {
      const index = this.clockValues.indexOf(value);
      const angle = (index / this.clockValues.length) * 2 * Math.PI;

      const radius = 100;

      const x = Math.sin(angle) * radius;
      const y = -Math.cos(angle) * radius;

      return {
        transform: `translate(-50%, -50%) translate(${x}px, ${y}px)`,
      };
    },
    select(value) {
      if (this.mode === "hour") {
        this.hour = value;
        this.mode = "minute";
        this.automatic = true;
      } else {
        this.minute = value;
      }
      this.emit();
    },
    onClickOutside() {
      if (this.open) {
        this.open = false;
      }
    },
    setPeriod(p) {
      this.period = p;
      this.emit();
    },
    emit() {
      const date = new Date();
      const offset = this.period === "PM" ? 12 : 0;
      date.setHours(this.hour + offset, this.minute);
      this.$emit("input", date);
    },
    onClockClick(event) {
      if (this.mode === "minute" && this.automatic) {
        this.automatic = false;
        return;
      }

      const rect = event.currentTarget.getBoundingClientRect();

      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;

      const clickX = event.clientX;
      const clickY = event.clientY;

      const dx = clickX - centerX;
      const dy = clickY - centerY;

      let angle = Math.atan2(dx, -dy);

      if (angle < 0) angle += 2 * Math.PI;

      const minutes = Math.round((angle / (2 * Math.PI)) * 60) % 60;

      this.minute = String(minutes).padStart(2, "0");

      this.emit();
    },
  },
};
</script>

<style>
.tp-input-time input:hover {
  cursor: pointer;
}
</style>

<style scoped>
.timepicker-dropdown {
  position: relative;
}

.picker-wrapper {
  position: absolute;
  width: 100%;
  z-index: 5;
  background-color: white;
  padding-top: 10px;
  padding-bottom: 10px;
  border: solid 1px;
}

.time-picker {
  margin: auto;
  width: 280px;
  font-family: Arial, sans-serif;
}

.header {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
}

.box {
  background: #eee;
  padding: 10px 16px;
  font-size: 32px;
  border-radius: 8px;
  cursor: pointer;
  margin-bottom: 0;
}

.box.active {
  background: #f1e1ff;
  color: #7c1fff;
}

.colon {
  font-size: 32px;
  margin: 0 6px;
}

.ampm {
  display: flex;
  flex-direction: column;
  margin-left: 10px;
}

.ampm button {
  border: none;
  background: #eee;
  padding: 6px;
  cursor: pointer;
}

.ampm .active {
  background: #e4ccff;
  color: #7c1fff;
}

.clock {
  position: relative;
  width: 260px;
  height: 260px;
  background: #e5e5e5;
  border-radius: 50%;
  margin: auto;
}

.number {
  position: absolute;
  top: 50%;
  left: 50%;
  font-size: 16px;
  cursor: pointer;
  user-select: none;
}

.hand {
  position: absolute;
  width: 2px;
  height: 90px;
  background: #7c1fff;
  top: 50%;
  left: 50%;
  transform-origin: bottom center;
  transform: translate(-50%, -100%) rotate(0deg);
  border-radius: 2px;
}

.center {
  position: absolute;
  width: 12px;
  height: 12px;
  background: #7c1fff;
  border-radius: 50%;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}
</style>
